import { Injectable } from '@angular/core';
import { BaseFacilityService } from './base-facility.service';
import { CommunicationService } from '../../../services/communication.service';
import { UrlSegments } from '../../jurisdiction.constants';
import {
  BaseDataObject,
  FurnaceDataObject,
  GasDeviceData,
  GasDeviceObject,
  GasFacilityData,
  GasFacilityResponse,
  GasFiringSystemData,
  GasFiringSystemResponse,
  GasFurnaceData,
  GasMeterData,
  MeterDataObject,
  OldMeterData,
} from '../../facilities-form/facilities-form.domain';
import { Observable, forkJoin } from 'rxjs';
import { defaultIfEmpty, map, mergeMap } from 'rxjs/operators';
import { EnvironmentService } from '../../../services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class GasFacilityService extends BaseFacilityService {
  constructor(communicationService: CommunicationService, private environmentService: EnvironmentService) {
    super(communicationService, {
      UpdateInstallationUrl: UrlSegments.UpdateGasInstallation,
      UpdateFacilityUrl: UrlSegments.UpdateGasFacility,
      UpdateDeviceUrl: UrlSegments.UpdateGasDevice,
      CreateDeviceUrl: UrlSegments.CreateGasDevice,
      CreateMeterUrl: UrlSegments.CreateGasMeter,
      UpdateMeterUrl: UrlSegments.UpdateGasMeter,
      UpdateOldMeterUrl: UrlSegments.UpdateOldGasMeter,
      CreateOldMeterUrl: UrlSegments.CreateOldGasMeter,
    });
  }

  protected createFacility(request: any): Observable<string> {
    return this.communicationService
      .postData<GasFacilityResponse>(UrlSegments.CreateGasFacility, request)
      .pipe(map(response => response.gasfacility.find(() => true).facility_id));
  }

  protected mapToFacilityRequest(facility: BaseDataObject): GasFacilityData {
    return {
      commercial_sector: facility.sector,
      sk_device_location: facility.placeOfMeter,
      sk_gas_facility_type: facility.facilityType,
      sk_gas_usage_for: facility.gasUsageFor || null,
      total_power_required_old: facility.oldPowerGridConnection,
      total_power_required_new: facility.newPowerGridConnection,
      transaction_no: facility.noOfConnections,
      other_location_details: facility.otherLocation,
      ...(this.environmentService.showChimneySweepConfirmationSection && {
        sk_chim_sweep_confirm: (facility as any).chimneySweepConfirm,
      }),
      ...(facility.totalHeatOutput && {
        total_power: facility.totalHeatOutput,
      }),
      ...(facility.isPowerConnectionForRemoteMeterReadingAvailable && {
        power_supply_available: facility.isPowerConnectionForRemoteMeterReadingAvailable,
      }),
      ...(facility.reasonForRequest && {
        reason_for_request: facility.reasonForRequest,
      }),
    };
  }

  protected get hasDevices(): boolean {
    return this.environmentService.showGasDeviceSection;
  }

  protected mapToFacilityDeviceRequest(device: GasDeviceObject): GasDeviceData {
    const data = {
      ...device,
      ...device.customFields,
    };

    return {
      ce_number: data.ceNumber,
      name: data.deviceName,
      power: data.power,
      device_removed: data.deviceRemovedCheckbox ? '1' : '0',
      sk_selected_gas_category: data.selectedGasCategory,
      gas_device_type: data.applianceType,
      sk_device_manufacturer: data.manufacturer,
      device_manufacturer_other: data.manufacturer === 'other' ? data.otherManufacturer : '',
      type: data.type,
      construction_year: data.manufactureYear,
      sk_device_category: data.applianceCategory,
      ...(data.requiredFlowPressure && { sk_required_flow_pressure: data.requiredFlowPressure }),
      ...(data.otherFlowPressure && { other_pressure: data.otherFlowPressure }),
    };
  }

  protected mapToMeterRequest(data: MeterDataObject): GasMeterData {
    return {
      consumption_forecast: data.annualConsumptionForecast,
      meter_count: data.amount,
      sk_communication: data.selectComm,
      sk_gas_meter_type: data.meterType,
      // sk_meter_mount: data.selectMount,
      sk_gas_meter_size: data.counterSize,
      sk_mounting_alignment: data.alignment,
      remote_readout: data.remoteReadoutCheckbox ? '1' : '0',
      ...(data.location && {
        position: data.location,
      }),
      ...(this.environmentService.showOldMeterField && { old_meter_number: data.oldMeterNumber }),
    };
  }

  protected mapToOldMeterRequest(data: MeterDataObject): OldMeterData {
    return {
      replaced_meter_no: data.meterNumber,
      replaced_meter_reading: data.meterReading,
      replaced_meter_image_id: data.meterImageId,
      replaced_meter_action: data.meterAction,
    };
  }

  protected getDevices(facility: BaseDataObject): any[] {
    return facility.gasDevice || [];
  }

  public saveAdditionalData(technicalData: any, installationId: string): Observable<any> {
    const firingSystemData = technicalData.firingSystemData;
    const construction = technicalData.construction;

    const gasFiringData = {
      ...firingSystemData,
      ...construction,
      ...technicalData.exhaustSystem,
      ...technicalData.releases,
      ...technicalData.meterInstallation,
    };

    return this.saveGasFiringData(gasFiringData, installationId).pipe(
      mergeMap(systemId => this.saveGasFurnaceData(technicalData.fireplaceVentilation, systemId))
    );
  }

  private saveGasFiringData(gasFiringData: any, installationId: string): Observable<any> {
    const request = this.mapToGasFiringDataRequest(gasFiringData);
    request.installation_id = installationId;

    const systemId = gasFiringData.systemId;
    if (systemId) {
      return this.communicationService.updateData(`${UrlSegments.UpdateGasFiringSystem}=${systemId}`, request).pipe(map(() => systemId));
    }

    return this.communicationService
      .postData<GasFiringSystemResponse>(UrlSegments.CreateGasFiringSystem, request)
      .pipe(map(response => response.fireingsystem.find(() => true).system_id));
  }

  private mapToGasFiringDataRequest(gasFiringData: any): GasFiringSystemData {
    return {
      sk_construction_spec: gasFiringData.constructionChimneySweep,
      chimney: gasFiringData.chimneyCheckbox ? '1' : '0',
      single_occupancy: gasFiringData.singleOccupancyCheckbox ? '1' : '0',
      vacuum_operation: gasFiringData.vacuumOperationCheckbox ? '1' : '0',
      moisture_sensitive: gasFiringData.moistureSensitiveCheckbox ? '1' : '0',
      moisture_insensitive: gasFiringData.notMoistureSensitiveCheckbox ? '1' : '0',
      emission_gas_line: gasFiringData.emissionGasLineCheckbox ? '1' : '0',
      overpressure_operation: gasFiringData.overPressureOperationCheckbox ? '1' : '0',
      multiple_occupancy: gasFiringData.multipleOccupancyCheckbox ? '1' : '0',
      air_emission_system: gasFiringData.airEmissionSystemCheckbox ? '1' : '0',
      proof_of_usability_type: gasFiringData.systemUsabilityCheckbox ? '1' : '0',
      proof_of_usability_comment: gasFiringData.systemUsabilityTextValue,
      assembly_line: gasFiringData.assemblyLineCheckbox ? '1' : '0',
      innershell_bulding_material: gasFiringData?.innerShell?.buildingMaterial,
      insualtion_bulding_material: gasFiringData?.insulatingMaterial?.buildingMaterial,
      ringgap_bulding_material: gasFiringData?.ringGap?.buildingMaterial,
      outershell_bulding_material: gasFiringData?.shaft?.buildingMaterial,
      innershell_thickness: gasFiringData?.innerShell?.thicknessInCm,
      insualtion_thickness: gasFiringData?.insulatingMaterial?.thicknessInCm,
      ringgap_thickness: gasFiringData?.ringGap?.thicknessInCm,
      sk_outershell_thickness: gasFiringData?.shaft?.thicknessInCm,
      innershell_manufacturer: gasFiringData?.innerShell?.producerType,
      insualtion_manufacturer: gasFiringData?.insulatingMaterial?.producerType,
      ringgap_manufacturer: gasFiringData?.ringGap?.producerType,
      outershell_manufacturer: gasFiringData?.shaft?.producerType,
      innershell_proof_of_usability: gasFiringData?.innerShell?.proofOfUsability,
      insualtion_proof_of_usability: gasFiringData?.insulatingMaterial?.proofOfUsability,
      ringgap_proof_of_usability: gasFiringData?.ringGap?.proofOfUsability,
      outershell_proof_of_usability: gasFiringData?.shaft?.proofOfUsability,
      effective_height: gasFiringData?.effectiveHeight?.replace(',', '.'),
      pipe_width: gasFiringData.chimney,
      sk_pipe_with_unit: gasFiringData.fireplace,
      sk_assessment_base: gasFiringData.dimension,
      assessment_base_din_comment: gasFiringData.din,
      combustion_with_ventilation: gasFiringData.withBlowerCheckbox ? '1' : '0',
      combustion_without_ventilation: gasFiringData.withoutBlowerCheckbox ? '1' : '0',
      combustion_with_flowprotection: gasFiringData.withFlowProtectionCheckbox ? '1' : '0',
      combustion_without_flowprotection: gasFiringData.withoutFlowProtectionCheckbox ? '1' : '0',
      combustion_air_supply_room: gasFiringData.airSupplyInstallationRoomCheckbox ? '1' : '0',
      combustion_air_supply_open: gasFiringData.airSupplyOpenAirCheckbox ? '1' : '0',
      combustion_exhaust_combair: gasFiringData.emissionGasCheckbox ? '1' : '0',
      sk_combustion_boiler_ventilation_type: !gasFiringData.airSupplyOpenAirCheckbox ? gasFiringData.roomVentilation : '',
      combustion_boiler_ventilation: !gasFiringData.airSupplyOpenAirCheckbox ? gasFiringData.roomVentilationValueincm : '',
      written_approval_client: gasFiringData.approvalClient ? '1' : '0',
      written_approval_planner: gasFiringData.approvalPlanner ? '1' : '0',
      has_gasmeter_installation: gasFiringData.meterInstallation,
    };
  }

  private saveGasFurnaceData(fireplaceVentilation: any, systemId: string): Observable<any> {
    const firePlaceVentilationData: FurnaceDataObject[] = (fireplaceVentilation && fireplaceVentilation.furnace) || [];

    return forkJoin(firePlaceVentilationData.map(furnaceItem => this.saveFurnaceItem(furnaceItem, systemId))).pipe(defaultIfEmpty([]));
  }

  private saveFurnaceItem(furnaceItem: FurnaceDataObject, systemId: string): Observable<any> {
    const request = this.mapToGasFurnaceRequest(furnaceItem);
    if (furnaceItem.furnaceId) {
      return this.communicationService.updateData(`${UrlSegments.UpdateGasFurnace}=${furnaceItem.furnaceId}`, request);
    }

    request.system_id = systemId;
    return this.communicationService.postData(UrlSegments.CreateGasFurnace, request);
  }

  private mapToGasFurnaceRequest(furnaceItem: FurnaceDataObject): GasFurnaceData {
    return {
      manufacturer: furnaceItem.producerType,
      type: furnaceItem.furnace,
      proof_of_usability: furnaceItem.proofOfUsability,
      room_volume: furnaceItem.volume,
      emission_temp: furnaceItem.temp,
      sk_fuel_type: furnaceItem.fuel,
      rated_heat1: furnaceItem.heat,
      rated_heat2: furnaceItem.setHeat,
      combustion_other_fuel: furnaceItem.otherFuelType,
    };
  }
}
